import React, { useEffect, useCallback, useRef, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Resizable } from 're-resizable';
import { LoadPanel } from 'devextreme-react/load-panel';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import DataGrid, {
  Column,
  Editing,
  Pager,
  Paging,
  Scrolling,
  Selection,
} from 'devextreme-react/data-grid';
import {
  setSelectedCustomShape,
  resetChanges,
  setChanges,
  setEditRowKey,
  setGridIsLoading,
  updateShape,
  deleteShape,
  appNotify
} from 'store/actions';

const AssumptionsGrid = ({
  gridRef,
  selectedGroup,
  selectedCustomShape,
  selectedGroupId,
  changes,
  editRowKey,
  gridIsLoading,
  resetChanges,
  setSelectedCustomShape,
  setChanges,
  setEditRowKey,
  setGridIsLoading,
  updateShape,
  deleteShape,
  appNotify
}) => {
  // == constants ===============================================

  const loadPanelPosition = { of: '#dataGrid' };
  const allowedPageSizes = [5, 10, 15, 20];

  // == useRef ================================================

  const gridResizable = useRef(null);

  // == useState ==============================================

  const [dataSource, setDataSource] = useState(null);

  // == useEffect =============================================

  useEffect(() => {
    if (!selectedGroup) {
      return;
    }

    const newArrayStore = new ArrayStore({
      key: 'shapeId',
      data: selectedGroup.shapes.map((shape) => {
        const dataRow = {
          shapeId: shape.shapeId,
          shapeName: shape.shapeName,
        };

        dataRow['numDetails'] = shape.details.filter(
          (detail) => detail.found
        ).length;

        selectedGroup.fields.map((field, index) => {
          const fieldValue = shape.fieldValues.find(
            (fieldValue) => fieldValue.fieldId === field.fieldId
          );

          let value = field.defaultValue;
          const fieldId = getFieldId(field, index);

          if (
            fieldValue &&
            fieldValue.value &&
            fieldValue.value !== field.defaultValue
          ) {
            value = fieldValue.value;
          }

          dataRow[fieldId] = value;
        });

        return dataRow;
      }),
    });

    const newDataSource = new DataSource({
      store: newArrayStore,
      reshapeOnPush: true,
    });

    setDataSource(newDataSource);
  }, [selectedGroup]);

  useEffect(() => {
    if (gridRef.current) {
      //hack, otherwise first focusedRowKey doesn't work
      gridRef.current.instance.option('focusedRowIndex', 0);
    }
  }, [gridRef.current]);

  useEffect(() => {
    if (selectedCustomShape == null) {
      return;
    }
    if (gridRef.current) {
      gridRef.current.instance.option(
        'focusedRowKey',
        selectedCustomShape.shapeId
      );
      
      if (!selectedCustomShape.polygons || !selectedCustomShape.boundingBox) {
        appNotify(`${selectedCustomShape.shapeName} has no shape file.`, 'warning');
      }
    }
  }, [gridRef, selectedCustomShape]);

  // == Event Handlers ================================================

  const onCellClicked = (e) => {
    if (e.rowType === 'data') {
      setSelectedCustomShape(e.data.shapeId);
    }
  };

  const onSaving = (e) => {
    setGridIsLoading(true);
    e.cancel = true;

    if (e.changes.length > 0) {
      const changes = e.changes[0];
      if (changes.type === 'remove') {
        deleteShape({ shapeId: changes.key, selectedGroupId });
      } else {
        updateShapeChanges(changes);
      }

      resetChanges();
    }

    setGridIsLoading(false);
  };

  const onChangesChange = useCallback((changes) => {
    setChanges(changes);
  }, []);

  const onEditRowKeyChange = useCallback((editRowKey) => {
    setEditRowKey(editRowKey);
  }, []);

  // == HELPERS =======================================================

  const updateShapeChanges = (changes) => {
    var shape = selectedGroup.shapes.find((s) => s.shapeId === changes.key);

    if (shape) {
      let updatedShape = { ...shape };
      let updatedFieldValues = [...shape.fieldValues];

      for (const [key, value] of Object.entries(changes.data)) {
        if (key === 'shapeName') {
          updatedShape.shapeName = value;
        } else {
          var index = updatedFieldValues.findIndex(
            (fv) => fv.fieldId === parseInt(key)
          );

          if (index >= 0) {
            updatedFieldValues[index] = {
              ...updatedFieldValues[index],
              value: value,
            };
          }
        }
      }

      updatedShape.fieldValues = updatedFieldValues;
      updateShape(updatedShape);
    }
  };

  const getFieldId = (field, index) => {
    return field.fieldId ? field.fieldId.toString() : `temp${index}`;
  };

  // == Components ====================================================

  return (
    <>
      <Resizable
        ref={gridResizable}
        // Use vh/vw or px only, not %.
        // Limitation of re-resizable- % of parent does not calculate correctly in a vertical flex layout
        minHeight='20vh'
        maxHeight='70vh'
        defaultSize={{ height: '30vh' }}
        enable={{
          top: true,
          right: false,
          bottom: false,
          left: false,
          topRight: false,
          bottomRight: false,
          bottomLeft: false,
          topLeft: false,
        }}
      >
        <LoadPanel position={loadPanelPosition} visible={gridIsLoading} />
        <DataGrid
          id='dataGrid'
          ref={gridRef}
          allowColumnResizing
          showRowLines
          showColumnLines
          width='100%'
          height='100%'
          columnResizingMode='widget'
          key='shapeId'
          dataSource={dataSource}
          focusedRowEnabled={true}
          hoverStateEnabled={true}
          onCellClick={onCellClicked}
          onSaving={onSaving}
        >
          <Editing
            mode='row'
            allowUpdating={!selectedGroup.wasPublished}
            allowDeleting={!selectedGroup.wasPublished}
            changes={changes}
            onChangesChange={onChangesChange}
            editRowKey={editRowKey}
            // allowUpdating={}
            onEditRowKeyChange={onEditRowKeyChange}
          />
          <Column key='shapeName' caption='Shape name' dataField='shapeName' />
          <Column
            key='numDetails'
            caption='Total Shapes'
            dataField='numDetails'
          />
          {selectedGroup.fields.map((field, index) => {
            const fieldId = getFieldId(field, index);
            return (
              <Column
                key={fieldId}
                caption={field.name}
                dataField={fieldId}
                dataType={field.assumptionFieldDataType.toLowerCase()}
              />
            );
          })}
          <Selection mode='single' />
          <Scrolling rowRenderingMode='virtual'></Scrolling>
          <Paging enabled={true} defaultPageSize={10} />
          <Pager
            visible='auto'
            allowedPageSizes={allowedPageSizes}
            displayMode='full'
            showPageSizeSelector={true}
            showNavigationButtons={true}
          />
        </DataGrid>
      </Resizable>
    </>
  );
};

// == Connect =================================================

const mapStateToProps = ({ assumptionModulesState, assumptionGroupsState, assumptionGridState }) => ({
  selectedGroup: assumptionGroupsState.selectedGroup,
  selectedGroupId: assumptionGroupsState.selectedGroupId,
  selectedCustomShape: assumptionGroupsState.selectedCustomShape,
  changes: assumptionGridState.changes,
  editRowKey: assumptionGridState.editRowKey,
  gridIsLoading: assumptionGridState.gridIsLoading,
});

const mapDispatchToProps = {
  setSelectedCustomShape,
  resetChanges,
  setChanges,
  setEditRowKey,
  setGridIsLoading,
  updateShape,
  deleteShape,
  appNotify
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  AssumptionsGrid
);
